import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Divider,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import { managerApi, type ManagerSubscription } from '../../api/manager_api';
import { useManagerUser } from './ManagerUserPage';

// Human-readable labels for roles
const ROLE_LABELS: Record<string, string> = {
  'client': 'Client',
  'assistant': 'Assistant',
  'support': 'Support',
  'trusted-tester': 'Trusted Tester',
  'manager': 'Manager',
  'mentor': 'Mentor',
  'auto-mentor': 'Auto-Mentor',
  'dedicated': 'Dedicated Assistant',
  'sudo': 'Sudo',
};

// Format date for display
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

// Color mapping for role chips
const getRoleColor = (role: string): 'error' | 'warning' | 'primary' | 'secondary' | 'success' | 'default' => {
  switch (role) {
    case 'sudo': return 'error';
    case 'manager': return 'warning';
    case 'support': return 'primary';
    case 'mentor':
    case 'auto-mentor': return 'secondary';
    case 'dedicated': return 'success';
    default: return 'default';
  }
};

// Color for subscription status chip
const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'warning';
    case 'past_due': return 'warning';
    case 'canceled': return 'error';
    case 'unpaid': return 'error';
    default: return 'default';
  }
};

// Check if a date is in the future
const isDateInFuture = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  return date > new Date();
};

// Get billing provider URL
const getBillingProviderUrl = (
  stripeCustomerId: string | null | undefined,
  recurlyAccountCode: string | null | undefined,
  subscriptionService: string | null | undefined
): { url: string; name: string } | null => {
  const isProd = import.meta.env.PROD;

  // Use subscription_service as primary determinant
  if (subscriptionService === 'stripe' && stripeCustomerId) {
    const basePath = isProd
      ? 'https://dashboard.stripe.com/customers'
      : 'https://dashboard.stripe.com/test/customers';
    return {
      url: `${basePath}/${stripeCustomerId}`,
      name: 'Stripe',
    };
  }

  if (subscriptionService === 'recurly' && recurlyAccountCode) {
    const subdomain = isProd ? 'fancyhands' : 'fancyhands-test';
    return {
      url: `https://${subdomain}.recurly.com/accounts/${recurlyAccountCode}`,
      name: 'Recurly',
    };
  }

  // Fallback for users without subscription_service set
  if (recurlyAccountCode) {
    const subdomain = isProd ? 'fancyhands' : 'fancyhands-test';
    return {
      url: `https://${subdomain}.recurly.com/accounts/${recurlyAccountCode}`,
      name: 'Recurly',
    };
  }

  if (stripeCustomerId) {
    const basePath = isProd
      ? 'https://dashboard.stripe.com/customers'
      : 'https://dashboard.stripe.com/test/customers';
    return {
      url: `${basePath}/${stripeCustomerId}`,
      name: 'Stripe',
    };
  }

  return null;
};

export const ManagerUserOverview = observer(() => {
  const { userKey } = useParams<{ userKey: string }>();
  const { user, error } = useManagerUser();

  const [subscriptions, setSubscriptions] = useState<ManagerSubscription[]>([]);
  const [subsLoading, setSubsLoading] = useState(false);
  const [subsError, setSubsError] = useState<string | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      if (!user?.key) return;
      // Only fetch for clients (non-assistants)
      if (user.is_beta) return;

      setSubsLoading(true);
      setSubsError(null);

      try {
        const result = await managerApi.getSubscriptions(user.key);
        setSubscriptions(result.items);
      } catch (err) {
        setSubsError(err instanceof Error ? err.message : 'Failed to load subscriptions');
      } finally {
        setSubsLoading(false);
      }
    };

    fetchSubscriptions();
  }, [user?.key, user?.is_beta]);

  const handleSyncStripe = async () => {
    if (!user?.stripe_customer_id) return;

    setSyncLoading(true);
    setSyncSuccess(null);
    setSubsError(null);

    try {
      const result = await managerApi.syncStripeAccount(
        user.stripe_customer_id
      );
      setSubscriptions(result.subscriptions);
      const subCount = result.subscriptions.length;
      const paymentCount = result.payments?.length || 0;
      setSyncSuccess(
        `Synced ${subCount} subscription${subCount !== 1 ? 's' : ''} and ${paymentCount} payment${paymentCount !== 1 ? 's' : ''} from Stripe`
      );
    } catch (err) {
      setSubsError(err instanceof Error ? err.message : 'Failed to sync from Stripe');
    } finally {
      setSyncLoading(false);
    }
  };

  const handleSyncRecurly = async () => {
    if (!user?.recurly_account_code) return;

    setSyncLoading(true);
    setSyncSuccess(null);
    setSubsError(null);

    try {
      const result = await managerApi.syncRecurlyAccount(
        user.recurly_account_code
      );
      setSubscriptions(result.subscriptions);
      const subCount = result.subscriptions.length;
      const paymentCount = result.payments?.length || 0;
      setSyncSuccess(
        `Synced ${subCount} subscription${subCount !== 1 ? 's' : ''} and ${paymentCount} payment${paymentCount !== 1 ? 's' : ''} from Recurly`
      );
    } catch (err) {
      setSubsError(err instanceof Error ? err.message : 'Failed to sync from Recurly');
    } finally {
      setSyncLoading(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Profile Information</Typography>
            <Button
              variant="contained"
              component={Link}
              to={`/manager/user/${userKey}/edit`}
            >
              Edit
            </Button>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {user.first_name || user.last_name
                ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                : '\u2014'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            {user.emails && user.emails.length > 0 ? (
              user.emails.map((e) => (
                <Box key={e.email} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {e.email}
                  </Typography>
                  {e.is_primary && <Chip label="Primary" color="primary" size="small" />}
                  {e.status === 'pending' && (
                    <Chip
                      label={e.expired ? 'Link expired' : 'Pending confirmation'}
                      color="warning"
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body1">
                {user.primary_email || '\u2014'}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Account Status
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {user.is_disabled && (
              <Chip label="Disabled" color="error" size="small" />
            )}
            {user.is_beta && (
              <Chip label="Approved" color="success" size="small" />
            )}
            {!user.is_disabled && !user.is_beta && (
              <Typography variant="body2" color="text.secondary">
                No special status
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            User Roles
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {user.roles && user.roles.length > 0 ? (
              user.roles.map((role) => (
                <Chip
                  key={role}
                  label={ROLE_LABELS[role] || role}
                  color={getRoleColor(role)}
                  size="small"
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No roles assigned
              </Typography>
            )}
          </Box>

          {/* Subscription info for clients only */}
          {!user.is_beta && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Subscriptions
              </Typography>

              {/* User-level subscription info */}
              {(user.tasks_remaining !== undefined || user.next_billing_cycle_date) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Tasks Remaining
                    </Typography>
                    <Typography variant="body1">
                      {user.tasks_remaining ?? '\u2014'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Next Billing Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(user.next_billing_cycle_date)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Subscription records from Stripe/Recurly */}
              {subsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : subsError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {subsError}
                </Alert>
              ) : subscriptions.length === 0 ? (
                // No local subscription records - check if they have active billing
                isDateInFuture(user.next_billing_cycle_date) ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Subscription data not synced locally. Check billing provider for details.
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No subscriptions found
                  </Typography>
                )
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Updated</TableCell>
                        <TableCell>Plan</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscriptions.map((sub) => (
                        <TableRow key={sub.key}>
                          <TableCell>{formatDate(sub.date_updated)}</TableCell>
                          <TableCell>{sub.plan_name || '\u2014'}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>
                            {sub.service}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={sub.service_subscription_status || 'unknown'}
                              size="small"
                              color={getStatusColor(sub.service_subscription_status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Sync success message */}
              {syncSuccess && (
                <Alert severity="success" sx={{ mt: 1 }} onClose={() => setSyncSuccess(null)}>
                  {syncSuccess}
                </Alert>
              )}

              {/* Billing provider link and sync button */}
              {(() => {
                const billingProvider = getBillingProviderUrl(
                  user.stripe_customer_id,
                  user.recurly_account_code,
                  user.subscription_service
                );
                if (!billingProvider) return null;
                return (
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      href={billingProvider.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon />}
                    >
                      View in {billingProvider.name}
                    </Button>
                    {billingProvider.name === 'Stripe' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSyncStripe}
                        disabled={syncLoading}
                        startIcon={syncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                      >
                        {syncLoading ? 'Syncing Stripe...' : 'Sync Stripe'}
                      </Button>
                    )}
                    {billingProvider.name === 'Recurly' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSyncRecurly}
                        disabled={syncLoading}
                        startIcon={syncLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                      >
                        {syncLoading ? 'Syncing Recurly...' : 'Sync Recurly'}
                      </Button>
                    )}
                  </Box>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
});
